<template>
    <div class="w-full space-y-3">
        <Head title="Reports"/>
        <div class="flex justify-between mt-2">
            <h1 class="mt-2 text-2xl font-bold leading-7 text-gray-900 sm:text-3xl">Fixture Report</h1>
            <a :href="route('frontend.reports.finance_fixture_report_export', {
                _query: {
                    search: form.search,
                    starts_at: form.starts_at,
                    ends_at: form.ends_at,
                    trading_entity: form.trading_entity
                }})" class="py-3 px-3 border border-transparent shadow-sm text-sm leading-4 rounded-md text-white bg-pitch-purple focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-amaze-coral w-40 text-center">Export All</a>
        </div>
    </div>
    <br/>
    <div class="grid grid-cols-4 gap-2">
        <div class="relative" v-if="trading_details.length > 1">
            <label for="trading-entity" class="block text-sm font-medium text-gray-700">Trading Entity</label>
            <select id="trading-entity" v-model="form.trading_entity"
                class="mt-2 bg-gray-50 border border-gray-300 text-gray-900 sm:text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500">
                <option value="" v-html="'All'"></option>
                <option v-for="(trading_detail, index) in trading_details" :key="index" :value="trading_detail.id" :selected="form.trading_entity === trading_detail.id" v-html="trading_detail.trading_name"></option>
            </select>
        </div>
        <div class="relative">
            <label  for="start-date" class="block text-sm font-medium text-gray-700">Start Date <span v-if="formattedStartDate">({{ formattedStartDate }})</span></label>
            <input v-model="form.starts_at" name="start-date" type="date" class="mt-2 bg-gray-50 border border-gray-300 text-gray-900 sm:text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500 datepicker-input" placeholder="Select start date">
        </div>
        <div class="relative">
            <label for="end-date" class="block text-sm font-medium text-gray-700">End Date <span v-if="formattedEndDate">({{ formattedEndDate }})</span></label>
            <input v-model="form.ends_at" name="end-date" type="date" class="mt-2 bg-gray-50 border border-gray-300 text-gray-900 sm:text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500 datepicker-input" placeholder="Select end date">
        </div>
        <div class="relative mt-5">
            <label for="end-date" class="sr-only">Search</label>
            <search-filter :show-filter="false" v-model:value="form.search" class="w-full p-2.5" @reset="reset"></search-filter>
        </div>
    </div>
    <br/>
    <div class="bg-white py-6 px-4 sm:p-6 shadow mb-6">
        <div class="flex flex-col mt-3 space-y-3">
            <div class="-my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
                <div class="py-2 align-middle inline-block min-w-full sm:px-6 lg:px-8">
                    <div class="shadow overflow-hidden border-b border-gray-200 sm:rounded-lg">
                        <table class="min-w-full divide-y divide-gray-200">
                            <thead class="bg-gray-50">
                                <tr>
                                    <th scope="col"
                                        class="px-6 py-3 text-left text-xs text-gray-500 uppercase tracking-wider">
                                        Fixture Code
                                    </th>
                                    <th scope="col"
                                        class="px-6 py-3 text-left text-xs text-gray-500 uppercase tracking-wider">
                                        Fixture Date
                                    </th>
                                    <th scope="col"
                                        class="px-6 py-3 text-left text-xs text-gray-500 uppercase tracking-wider">
                                        Fixture Name
                                    </th>
                                    <th scope="col"
                                        class="px-6 py-3 text-left text-xs text-gray-500 uppercase tracking-wider">
                                        Fixture Owner
                                    </th>
                                    <th scope="col"
                                        class="px-6 py-3 text-left text-xs text-gray-500 uppercase tracking-wider">
                                        Date Created
                                    </th>
                                    <th scope="col"
                                        class="px-6 py-3 text-left text-xs text-gray-500 uppercase tracking-wider">
                                        Quoted Costs
                                    </th>
                                    <th scope="col"
                                        class="px-6 py-3 text-left text-xs text-gray-500 uppercase tracking-wider">
                                        Actual Costs
                                    </th>
                                    <th scope="col"
                                        class="px-6 py-3 text-left text-xs text-gray-500 uppercase tracking-wider">
                                        Difference in Costs
                                    </th>
                                    <th scope="col"
                                        class="px-6 py-3 text-left text-xs text-gray-500 uppercase tracking-wider">
                                        Quoted Commission
                                    </th>
                                    <th scope="col"
                                        class="px-6 py-3 text-left text-xs text-gray-500 uppercase tracking-wider">
                                        Actual Commission
                                    </th>
                                    <th scope="col"
                                        class="px-6 py-3 text-left text-xs text-gray-500 uppercase tracking-wider">
                                        Difference in Commission
                                    </th>
                                    <th scope="col"
                                        class="px-6 py-3 text-left text-xs text-gray-500 uppercase tracking-wider">
                                        Quoted Revenue
                                    </th>
                                    <th scope="col"
                                        class="px-6 py-3 text-left text-xs text-gray-500 uppercase tracking-wider">
                                        Actual Revenue
                                    </th>
                                    <th scope="col"
                                        class="px-6 py-3 text-left text-xs text-gray-500 uppercase tracking-wider">
                                        Difference in Revenue
                                    </th>
                                    <th scope="col"
                                        class="px-6 py-3 text-left text-xs text-gray-500 uppercase tracking-wider">
                                        Fixture Estimate
                                    </th>
                                    <th scope="col"
                                        class="px-6 py-3 text-left text-xs text-gray-500 uppercase tracking-wider">
                                        Fixture Cost
                                    </th>
                                    <th scope="col"
                                        class="px-6 py-3 text-left text-xs text-gray-500 uppercase tracking-wider">
                                        Difference in Cost and Estimate
                                    </th>
                                    <th scope="col"
                                        class="px-6 py-3 text-left text-xs text-gray-500 uppercase tracking-wider">
                                        Profit
                                    </th>
                                    <th scope="col"
                                        class="px-6 py-3 text-left text-xs text-gray-500 uppercase tracking-wider">
                                        Profit Percentage
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-if="Object.values(fixtures).length > 0" v-for="(fixture, index) in fixtures" :key="index">
                                    <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                        {{ fixture.reference }}
                                    </td>
                                    <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                        {{ moment(fixture.starts_at).format('DD MMM YYYY') }}
                                    </td>
                                    <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                        {{ fixture.title }}
                                    </td>
                                    <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                        {{ fixture.travel_manager?.name ?? "" }}
                                    </td>
                                    <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                        {{ moment(fixture.created_at).format('DD MMM YYYY') }}
                                    </td>
                                    <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                        <span v-if="fixture.currency">
                                            {{ formatCurrency(fixture.quoted_costs, fixture.currency.code, true) }}
                                        </span>
                                        <span v-else>
                                            Currency Undefinded
                                        </span>
                                    </td>
                                    <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                        <span v-if="fixture.currency">
                                            {{ formatCurrency(fixture.actual_costs, fixture.currency.code, true) }}
                                        </span>
                                        <span v-else>
                                            Currency Undefinded
                                        </span>
                                    </td>
                                    <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                        <span v-if="fixture.currency">
                                            {{ formatCurrency(fixture.quoted_costs - fixture.actual_costs, fixture.currency.code, true) }}
                                        </span>
                                        <span v-else>
                                            Currency Undefinded
                                        </span>
                                    </td>
                                    <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                        <span v-if="fixture.currency">
                                            {{ formatCurrency(fixture.quoted_commission, fixture.currency.code, true) }}
                                        </span>
                                        <span v-else>
                                            Currency Undefinded
                                        </span>
                                    </td>
                                    <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                        <span v-if="fixture.currency">
                                            {{ formatCurrency(fixture.actual_commission, fixture.currency.code, true) }}
                                        </span>
                                        <span v-else>
                                            Currency Undefinded
                                        </span>
                                    </td>
                                    <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                        <span v-if="fixture.currency">
                                            {{ formatCurrency(fixture.quoted_commission - fixture.actual_commission, fixture.currency.code, true) }}
                                        </span>
                                        <span v-else>
                                            Currency Undefinded
                                        </span>
                                    </td>
                                    <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                        <span v-if="fixture.currency">
                                            {{ formatCurrency(fixture.quoted_revenue, fixture.currency.code, true) }}
                                        </span>
                                        <span v-else>
                                            Currency Undefinded
                                        </span>
                                    </td>
                                    <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                        <span v-if="fixture.currency">
                                            {{ formatCurrency(fixture.actual_revenue, fixture.currency.code, true) }}
                                        </span>
                                        <span v-else>
                                            Currency Undefinded
                                        </span>
                                    </td>
                                    <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                        <span v-if="fixture.currency">
                                            {{ formatCurrency(fixture.quoted_revenue - fixture.actual_revenue, fixture.currency.code, true) }}
                                        </span>
                                        <span v-else>
                                            Currency Undefinded
                                        </span>
                                    </td>
                                    <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                        <span v-if="fixture.currency">
                                            {{ formatCurrency(fixture.estimated_cost, fixture.currency.code, true) }}
                                        </span>
                                        <span v-else>
                                            Currency Undefinded
                                        </span>
                                    </td>
                                    <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                        <!-- Fixture Cost -->

                                        <!-- <span v-if="fixture.currency">
                                            {{ formatCurrency(fixture.profit, fixture.currency.code, true) }}
                                        </span>
                                        <span v-else>
                                            Currency Undefinded
                                        </span> -->
                                    </td>
                                    <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                        <!-- Difference in Fixture Cost and Estimate -->

                                        <!-- <span v-if="fixture.currency">
                                            {{ formatCurrency(fixture.estimated_cost - fixture.profit, fixture.currency.code, true) }}
                                        </span>
                                        <span v-else>
                                            Currency Undefinded
                                        </span> -->
                                    </td>
                                    <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                        <span v-if="fixture.currency">
                                            {{ formatCurrency(fixture.profit, fixture.currency.code, true) }}
                                        </span>
                                        <span v-else>
                                            Currency Undefinded
                                        </span>
                                    </td>
                                    <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                        <span v-if="fixture.profit_percentage == 0">
                                            0.00%
                                        </span>
                                        <span v-else>
                                            {{ fixture.profit_percentage }}%
                                        </span>
                                    </td>
                                </tr>
                                <tr v-if="Object.values(fixtures).length > 0">
                                    <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900 font-bold" colspan="5">
                                        User Totals
                                    </td>
                                    <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                        {{ formatCurrency(user_totals['quoted_costs'], 'GBP', true) }}
                                    </td>
                                    <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                        {{ formatCurrency(user_totals['actual_costs'], 'GBP', true) }}
                                    </td>
                                    <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                        {{ formatCurrency(user_totals['quoted_costs'] - user_totals['actual_costs'], 'GBP', true) }}
                                    </td>
                                    <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                        {{ formatCurrency(user_totals['quoted_commission'], 'GBP', true) }}
                                    </td>
                                    <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                        {{ formatCurrency(user_totals['actual_commission'], 'GBP', true) }}
                                    </td>
                                    <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                        {{ formatCurrency(user_totals['quoted_commission'] - user_totals['actual_commission'], 'GBP', true) }}
                                    </td>
                                    <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                        {{ formatCurrency(user_totals['quoted_revenue'], 'GBP', true) }}
                                    </td>
                                    <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                        {{ formatCurrency(user_totals['actual_revenue'], 'GBP', true) }}
                                    </td>
                                    <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                        {{ formatCurrency(user_totals['quoted_revenue'] - user_totals['actual_revenue'], 'GBP', true) }}
                                    </td>
                                    <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                        {{ formatCurrency(user_totals['fixture_estimate'], 'GBP', true) }}
                                    </td>
                                    <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                        <!-- Fixture Cost -->
                                    </td>
                                    <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                        <!-- Difference in Cost and Estimate -->
                                    </td>
                                    <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                        {{ formatCurrency(user_totals['fixture_profit'], 'GBP', true) }}
                                    </td>
                                    <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                        {{ user_totals['fixture_profit_percentage'] }}%
                                    </td>
                                </tr>
                                <tr v-if="Object.values(fixtures).length > 0">
                                    <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900 font-bold" colspan="5">
                                        Project Totals
                                    </td>
                                    <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                        {{ formatCurrency(totals['quoted_costs'], 'GBP', true) }}
                                    </td>
                                    <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                        {{ formatCurrency(totals['actual_costs'], 'GBP', true) }}
                                    </td>
                                    <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                        {{ formatCurrency(totals['quoted_costs'] - totals['actual_costs'], 'GBP', true) }}
                                    </td>
                                    <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                        {{ formatCurrency(totals['quoted_commission'], 'GBP', true) }}
                                    </td>
                                    <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                        {{ formatCurrency(totals['actual_commission'], 'GBP', true) }}
                                    </td>
                                    <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                        {{ formatCurrency(totals['quoted_commission'] - totals['actual_commission'], 'GBP', true) }}
                                    </td>
                                    <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                        {{ formatCurrency(totals['quoted_revenue'], 'GBP', true) }}
                                    </td>
                                    <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                        {{ formatCurrency(totals['actual_revenue'], 'GBP', true) }}
                                    </td>
                                    <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                        {{ formatCurrency(totals['quoted_revenue'] - totals['actual_revenue'], 'GBP', true) }}
                                    </td>
                                    <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                        {{ formatCurrency(totals['fixture_estimate'], 'GBP', true) }}
                                    </td>
                                    <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                        <!-- Fixture Cost -->
                                    </td>
                                    <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                        <!-- Difference in Cost and Estimate -->
                                    </td>
                                    <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                        {{ formatCurrency(totals['fixture_profit'], 'GBP', true) }}
                                    </td>
                                    <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                        <!-- {{ totals['total_profit_percentage'] }}% -->
                                    </td>
                                </tr>
                                <tr v-if="Object.values(fixtures).length > 0">
                                    <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900 font-bold" colspan="5">
                                        Percentage Breakdown
                                    </td>
                                    <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                        {{
                                            isNaN(user_totals['quoted_costs'] / totals['quoted_costs'])
                                            ? '0.00'
                                            : (user_totals['quoted_costs'] / totals['quoted_costs'] * 100).toFixed(2)
                                        }}%
                                    </td>
                                    <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                        {{
                                            isNaN(user_totals['actual_costs'] / totals['actual_costs'])
                                            ? '0.00'
                                            : (user_totals['actual_costs'] / totals['actual_costs'] * 100).toFixed(2)
                                        }}%
                                    </td>
                                    <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                        {{
                                            isNaN(user_totals['quoted_costs'] / totals['actual_costs'])
                                            ? '0.00'
                                            : (user_totals['quoted_costs'] / totals['actual_costs'] * 100).toFixed(2)
                                        }}%
                                    </td>
                                    <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                        {{
                                            isNaN(user_totals['quoted_commission'] / totals['quoted_commission'])
                                            ? '0.00'
                                            : (user_totals['quoted_commission'] / totals['quoted_commission'] * 100).toFixed(2)
                                        }}%
                                    </td>
                                    <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                        {{
                                            isNaN(user_totals['actual_commission'] / totals['actual_commission'])
                                            ? '0.00'
                                            : (user_totals['actual_commission'] / totals['actual_commission'] * 100).toFixed(2)
                                        }}%
                                    </td>
                                    <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                        {{
                                            isNaN(user_totals['quoted_commission'] / totals['actual_commission'])
                                            ? '0.00'
                                            : (user_totals['quoted_commission'] / totals['actual_commission'] * 100).toFixed(2)
                                        }}%
                                    </td>
                                    <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                        {{
                                            isNaN(user_totals['quoted_revenue'] / totals['quoted_revenue'])
                                            ? '0.00'
                                            : (user_totals['quoted_revenue'] / totals['quoted_revenue'] * 100).toFixed(2)
                                        }}%
                                    </td>
                                    <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                        {{
                                            isNaN(user_totals['actual_revenue'] / totals['actual_revenue'])
                                            ? '0.00'
                                            : (user_totals['actual_revenue'] / totals['actual_revenue'] * 100).toFixed(2)
                                        }}%
                                    </td>
                                    <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                        {{
                                            isNaN(user_totals['quoted_revenue'] / totals['actual_revenue'])
                                            ? '0.00'
                                            : (user_totals['quoted_revenue'] / totals['actual_revenue'] * 100).toFixed(2)
                                        }}%
                                    </td>
                                    <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                        {{
                                            isNaN(user_totals['fixture_estimate'] / totals['fixture_estimate'])
                                            ? '0.00'
                                            : (user_totals['fixture_estimate'] / totals['fixture_estimate'] * 100).toFixed(2)
                                        }}%
                                    </td>
                                    <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                        <!-- Fixture Cost -->
                                    </td>
                                    <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                        <!-- Difference in Cost and Estimate -->
                                    </td>
                                    <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                        {{
                                            isNaN(user_totals['fixture_profit'] / totals['fixture_profit'])
                                            ? '0.00'
                                            : (user_totals['fixture_profit'] / totals['fixture_profit'] * 100).toFixed(2)
                                        }}%
                                    </td>
                                    <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                        <!-- {{ user_totals['total_profit_percentage'] }}% -->
                                    </td>
                                </tr>
                                <tr v-else>
                                    <td colspan="8"
                                        class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                        There are no fixtures available for your search criteria.
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
    import Authenticated from "@/Layouts/Authenticated";
    import {Link, Head, useForm} from "@inertiajs/vue3";
    import Pagination from "@/Components/Pagination";
    import mapValues from "lodash/mapValues";
    import debounce from "lodash/debounce";
    import pickBy from "lodash/pickBy";
    import SearchFilter from "@/Components/SearchFilter";
    import moment from "moment/moment";

    export default {
        layout: Authenticated,
        components: {
            Head,
            Link,
            Pagination,
            SearchFilter
        },
        computed: {
            formattedStartDate() {
                if (this.form.starts_at) {
                    return moment(this.form.starts_at).format('DD MMM YYYY');
                }
                return '';
            },
            formattedEndDate() {
                if (this.form.ends_at) {
                    return moment(this.form.ends_at).format('DD MMM YYYY');
                }
                return '';
            },
        },
        created() {
            this.moment = moment;
        },
        props: [
            'fixtures', 'starts_at', 'ends_at', 'user_totals', 'totals', 'trading_details'
        ],
        data(props) {
            return {
                form: useForm({
                    search: null,
                    starts_at: props.starts_at,
                    ends_at: props.ends_at,
                    trading_entity: null
                })
            }
        },
        methods: {
            reset() {
                this.form = mapValues(this.form, () => null)
            },
        },
        created() {
            this.moment = moment
        },
        watch: {
            form: {
                deep: true,
                handler: debounce(function () {
                    this.$inertia.get(route('frontend.reports.finance_fixture_report'), pickBy(this.form), {preserveState: true, preserveScroll: true})
                }, 500),
            },
        }
    }
</script>
